package org.teddapp.project.ui

import androidx.compose.foundation.border
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.defaultMinSize
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp

/**
 * Boutons du design system. Le libellé commence par un verbe.
 *
 * - [Primary] : l'action principale de l'écran, une seule par vue.
 * - [Secondary] : action courante, contour [TeddTokens.LineStrong].
 * - [Ghost] : action discrète, sans fond au repos.
 * - [Publish] : tout ce qui rend public. Latérite, et rien d'autre ne l'est.
 * - [Danger] : suppression.
 *
 * Trois hauteurs : 32 (barres d'outils), 40 (défaut), 48 (action principale d'une session).
 */
enum class TeddButtonKind { Primary, Secondary, Ghost, Publish, Danger }
enum class TeddButtonSize { Sm, Md, Lg }

@Composable
fun TeddButton(
    text: String,
    onClick: () -> Unit,
    modifier: Modifier = Modifier,
    kind: TeddButtonKind = TeddButtonKind.Secondary,
    size: TeddButtonSize = TeddButtonSize.Md,
    enabled: Boolean = true,
    /** Nom d'une icône de [TeddIcon], posée avant le libellé. */
    icon: String? = null,
    /** Raccourci clavier affiché après le libellé. */
    kbd: String? = null,
    leadingIcon: (@Composable () -> Unit)? = null,
) {
    val height: Dp
    val padH: Dp
    val fontSize: Int
    when (size) {
        TeddButtonSize.Sm -> { height = TeddSize.ControlSm; padH = TeddSpace.S3; fontSize = 13 }
        TeddButtonSize.Md -> { height = TeddSize.ControlMd; padH = TeddSpace.S4; fontSize = 14 }
        TeddButtonSize.Lg -> { height = TeddSize.ControlLg; padH = TeddSpace.S6; fontSize = 15 }
    }
    val shape = RoundedCornerShape(if (size == TeddButtonSize.Lg) TeddTokens.RMd else TeddTokens.RSm)
    val colors = LocalTeddColors.current
    val fill: Color
    val hover: Color
    val content: Color
    when (kind) {
        TeddButtonKind.Primary -> { fill = colors.action; hover = colors.actionHover; content = colors.onAction }
        TeddButtonKind.Secondary -> { fill = colors.surface; hover = colors.surfaceSunken; content = colors.ink }
        TeddButtonKind.Ghost -> { fill = Color.Transparent; hover = colors.surfaceSunken; content = colors.ink }
        TeddButtonKind.Publish -> { fill = colors.laterite; hover = colors.laterite; content = colors.onLaterite }
        TeddButtonKind.Danger -> { fill = colors.danger; hover = colors.danger; content = colors.onDanger }
    }

    Row(
        modifier = modifier
            .teddClickable(onClick = onClick, shape = shape, fill = fill, hover = hover, enabled = enabled)
            .then(if (kind == TeddButtonKind.Secondary) Modifier.border(1.dp, colors.lineStrong, shape) else Modifier)
            .height(height)
            .defaultMinSize(minWidth = height)
            .padding(horizontal = padH),
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.spacedBy(TeddSpace.S2, Alignment.CenterHorizontally),
    ) {
        if (icon != null) TeddIcon(name = icon, size = if (size == TeddButtonSize.Sm) 16.dp else 18.dp, color = content)
        if (leadingIcon != null) leadingIcon()
        Text(
            text = text,
            color = content,
            style = TeddType.Label.copy(fontSize = fontSize.sp, fontWeight = FontWeight(540)),
            maxLines = 1,
        )
        if (kbd != null) TeddKbd(kbd, onFill = kind == TeddButtonKind.Primary, color = content)
    }
}

/** Bouton carré sans libellé, pour une icône. */
@Composable
fun TeddIconButton(
    onClick: () -> Unit,
    modifier: Modifier = Modifier,
    size: Dp = TeddSize.ControlSm,
    active: Boolean = false,
    content: @Composable () -> Unit,
) {
    Box(
        modifier = modifier
            .teddClickable(
                onClick = onClick,
                fill = if (active) TeddTokens.LimeSoft else Color.Transparent,
            )
            .height(size)
            .defaultMinSize(minWidth = size),
        contentAlignment = Alignment.Center,
    ) {
        content()
    }
}
